if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.bindViewerArrows();
        this.bindSwipeGestures();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      bindViewerArrows() {
        // The active image is toggled via display, so the viewer's built-in
        // scroll-based slider arrows can't move between slides. Drive them
        // through setActiveMedia instead, the same path thumbnail clicks use.
        const prevButton = this.elements.viewer.querySelector('.slider-buttons .slider-button[name="previous"]');
        const nextButton = this.elements.viewer.querySelector('.slider-buttons .slider-button[name="next"]');
        if (!prevButton || !nextButton) return;
        prevButton.addEventListener('click', this.onViewerArrowClick.bind(this, -1));
        nextButton.addEventListener('click', this.onViewerArrowClick.bind(this, 1));
      }

      onViewerArrowClick(direction, event) {
        event.preventDefault();
        this.goToMedia(direction);
      }

      goToMedia(direction) {
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]'));
        if (mediaItems.length < 2) return;
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        const currentIndex = activeIndex === -1 ? 0 : activeIndex;
        const targetIndex = (currentIndex + direction + mediaItems.length) % mediaItems.length;
        this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false, true);
      }

      bindSwipeGestures() {
        // The active image is toggled via display (not real horizontal scroll),
        // so swiping is handled manually: track the gesture, then step to the
        // previous/next media item once it passes a distance threshold.
        const track = this.elements.viewer.querySelector('.product__media-list');
        if (!track) return;

        // Prevent the browser's native image drag-ghost from hijacking the gesture.
        track.addEventListener('dragstart', (event) => event.preventDefault());

        const DRAG_THRESHOLD = 6;
        const SWIPE_THRESHOLD = 40;
        let tracking = false;
        let horizontal = false;
        let startX = 0;
        let startY = 0;
        let deltaX = 0;

        const start = (x, y) => {
          tracking = true;
          horizontal = false;
          startX = x;
          startY = y;
          deltaX = 0;
        };

        const move = (x, y, event) => {
          if (!tracking) return;
          const currentDeltaX = x - startX;
          const currentDeltaY = y - startY;

          if (!horizontal) {
            if (Math.abs(currentDeltaX) <= DRAG_THRESHOLD && Math.abs(currentDeltaY) <= DRAG_THRESHOLD) return;
            if (Math.abs(currentDeltaX) <= Math.abs(currentDeltaY)) {
              tracking = false;
              return;
            }
            horizontal = true;
            track.classList.add('is-dragging');
          }

          deltaX = currentDeltaX;
          if (event && event.cancelable) event.preventDefault();
        };

        const end = () => {
          if (!tracking) return;
          tracking = false;
          track.classList.remove('is-dragging');
          if (horizontal && Math.abs(deltaX) >= SWIPE_THRESHOLD) this.goToMedia(deltaX < 0 ? 1 : -1);
          horizontal = false;
        };

        track.addEventListener(
          'touchstart',
          (event) => {
            const touch = event.touches[0];
            start(touch.clientX, touch.clientY);
          },
          { passive: true }
        );

        track.addEventListener(
          'touchmove',
          (event) => {
            const touch = event.touches[0];
            move(touch.clientX, touch.clientY, event);
          },
          { passive: false }
        );

        track.addEventListener('touchend', end, { passive: true });
        track.addEventListener('touchcancel', end, { passive: true });

        track.addEventListener('mousedown', (event) => {
          if (event.button !== 0) return;
          start(event.clientX, event.clientY);

          const onMouseMove = (moveEvent) => move(moveEvent.clientX, moveEvent.clientY, moveEvent);
          const onMouseUp = () => {
            end();
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
          };
          document.addEventListener('mousemove', onMouseMove);
          document.addEventListener('mouseup', onMouseUp);
        });

        track.addEventListener(
          'click',
          (event) => {
            if (Math.abs(deltaX) > DRAG_THRESHOLD) {
              event.preventDefault();
              event.stopPropagation();
            }
          },
          true
        );
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, preventPageScroll = false) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if (preventPageScroll) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
